import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    InputModeOptions,
    StyleProp,
    StyleSheet,
    TextInput,
    TextInputEndEditingEvent,
    TextInputKeyPressEvent,
    ViewStyle,
    useWindowDimensions,
} from 'react-native';
import Animated from 'react-native-reanimated';

import IconCircledClose from '@/assets/icons/circled-close';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { isVariablePayoutEntry } from '@/hooks/use-entries-utils';
import { useGlobalState } from '@/hooks/use-global-state';
import { designSystem } from '@/styles/styles';
import { GameMode, GameType, MetadataType, ProjectionType, PromotionType } from '@/types/api.generated';
import { MAX_WEB_WIDTH, isWeb } from '@/utils/constants-platform-specific';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useFantasyDecimalsEnabled } from '../../hooks/use-fantasy-decimals-enabled';
import { FantasyEntryPromotionStatus } from '../../hooks/use-fantasy-entry-promotions';
import { useMysteryMode } from '../../hooks/use-mystery-mode';

const AnimatedTextInput = Animated.createAnimatedComponent(TextInput);

export const AmountInput = ({
    toWin,
    amount,
    showError,
    showKeyboardOnFocus = true,
    style,
    onFocusInput,
    onBlurInput,
    onEndEditing,
    onClearPress,
    onKeyPress,
    placeholder,
    initialFocusState = false,
    shouldHideClearIcon,
    promoApplied,
    inputMode,
    autoFocus,
    alwaysHideClearIcon,
    editable = true,
    amountInputLabel,
}: {
    initialFocusState?: boolean;
    toWin?: number;
    amount: string;
    showError?: boolean;
    showKeyboardOnFocus?: boolean;
    style?: StyleProp<ViewStyle>;
    onFocusInput?: () => void;
    onBlurInput?: () => void;
    onEndEditing?: (e: TextInputEndEditingEvent) => void;
    onClearPress?: () => void;
    onKeyPress?: (e: TextInputKeyPressEvent) => void;
    placeholder?: string;
    shouldHideClearIcon?: boolean;
    promoApplied?: FantasyEntryPromotionStatus;
    inputMode?: InputModeOptions;
    autoFocus?: boolean;
    alwaysHideClearIcon?: boolean;
    editable?: boolean;
    amountInputLabel?: string;
}) => {
    const { t } = useTranslation(['betslip_pickem']);
    const { width } = useWindowDimensions();
    const activeGameMode = useBetslipStore(state => state.activeGameMode);
    const gameType = useGlobalState(
        state => state.allEntryRules.find(rule => rule.gameMode === activeGameMode)?.gameType
    );
    //If P2P is available, this means this is an entry in a P2P state
    const p2pGameType = gameType === GameType.P2p;
    const decimalsEnabled = useFantasyDecimalsEnabled({ gameMode: activeGameMode });

    const smallerScreenOnWeb = isWeb && width <= MAX_WEB_WIDTH;

    const [isInputFocused, setIsInputFocused] = useState(initialFocusState);

    const isValidAmount = amount !== null && amount !== undefined && amount !== '' && amount !== '0';

    const onFocus = useCallback(() => {
        setIsInputFocused(true);
        onFocusInput?.();
    }, [onFocusInput]);

    const onBlur = useCallback(() => {
        setIsInputFocused(false);
        onBlurInput?.();
    }, [onBlurInput]);

    const { mysteryApplied } = useMysteryMode();
    const toWinLabel =
        isVariablePayoutEntry(activeGameMode, p2pGameType) || mysteryApplied ? t('wins_up_to') : t('to_win');

    const handleKeyPress = useCallback(
        (e: TextInputKeyPressEvent) => {
            const key = e.nativeEvent.key;
            // this prevents decimals use on web
            if (!decimalsEnabled && (key === '.' || key === ',')) {
                e.preventDefault?.();
                return;
            }
            onKeyPress?.(e);
        },
        [decimalsEnabled, onKeyPress]
    );

    const dollarSignColor = useMemo((): AppColors => {
        if (showError) {
            return ds.colorToken.text.danger;
        }
        if (isInputFocused || isValidAmount) {
            return ds.colorToken.text.primary;
        }
        return 'neutral300';
    }, [isInputFocused, isValidAmount, showError]);

    const hideClearIcon = shouldHideClearIcon || alwaysHideClearIcon;

    return (
        <Box
            my={'s16'}
            px={'s16'}
            borderWidth={1}
            borderColor={
                showError
                    ? ds.colorToken.background.danger.strong
                    : isInputFocused
                    ? ds.colorToken.background.brand.emphasis
                    : 'neutral500'
            }
            borderRadius={'r16'}
            style={[styles.container, style]}
        >
            <Row gap={'s16'}>
                <Box flex={smallerScreenOnWeb || alwaysHideClearIcon ? 1 : undefined}>
                    <Text variant={'bodySmall'} letterSpacing={-0.08} color={'neutral300'}>
                        {amountInputLabel}
                    </Text>
                    <Row alignContent={'center'} alignItems={'center'}>
                        <Text
                            numberOfLines={1}
                            variant={'bodyLarge'}
                            lineHeight={24}
                            letterSpacing={-0.43}
                            color={dollarSignColor}
                        >
                            $
                        </Text>

                        <AnimatedTextInput
                            enterKeyHint="done"
                            placeholder={placeholder}
                            keyboardAppearance="dark"
                            showSoftInputOnFocus={showKeyboardOnFocus}
                            style={[styles.input, showError && styles.inputError, !editable && styles.inputDisabled]}
                            onKeyPress={handleKeyPress}
                            onFocus={onFocus}
                            onEndEditing={onEndEditing}
                            onBlur={onBlur}
                            value={amount}
                            placeholderTextColor={designSystem.colors.neutral300}
                            inputMode={inputMode ?? (decimalsEnabled ? 'decimal' : 'numeric')}
                            autoFocus={autoFocus}
                            editable={editable}
                            autoCapitalize="none"
                            selectionColor={showError ? ds.colorValue.text.danger : ds.colorValue.text.brand}
                            selectionHandleColor={ds.colorValue.text.brand}
                            maxLength={10}
                        />
                    </Row>
                </Box>
                <LineSeparator vertical style={styles.separator} />
                <Row flex={1} alignItems={'center'} justifyContent={'space-between'}>
                    <Box>
                        <Text variant={'bodySmall'} letterSpacing={-0.08} color={'neutral300'}>
                            {toWinLabel}
                        </Text>
                        <Text
                            variant={'bodyLarge'}
                            letterSpacing={-0.43}
                            color={'neutral300'}
                            lineHeight={24}
                            testID={'to-win-value'}
                        >
                            {toWin ? toLocaleCurrency(toWin, true) : '$'}
                        </Text>
                    </Box>
                    {hideClearIcon ? null : (
                        <TouchableBox ml={'s16'} onPress={onClearPress} testID="clear-input">
                            <IconCircledClose />
                        </TouchableBox>
                    )}
                </Row>
            </Row>
            <ProfitBoostSection
                hasEntryAmount={amount || placeholder}
                activeGameMode={activeGameMode}
                activePromo={promoApplied}
                amount={amount}
            />
            <FreeEntrySection activePromo={promoApplied} />
            <FreePickSection />
        </Box>
    );
};

type ProfitBoostSectionProps = {
    hasEntryAmount?: string;
    activeGameMode: GameMode;
    activePromo?: FantasyEntryPromotionStatus;
    amount: string;
};
const ProfitBoostSection = memo(({ hasEntryAmount, activeGameMode, activePromo, amount }: ProfitBoostSectionProps) => {
    const isPerfectPlayGameMode = activeGameMode === GameMode.Perfect;

    const isPromoProfitBoostApplied = activePromo?.promotion.type === PromotionType.ProfitBoost;
    if (!isPromoProfitBoostApplied) return null;

    const metadata = activePromo?.promotion.metadata || [];
    const getMetadataValue = (type: MetadataType) => metadata.find(data => data.type === type)?.value;
    const profitPayoutAmount = getMetadataValue(MetadataType.PayoutAmount);
    const maxProfitPayoutAmount = getMetadataValue(MetadataType.MaxPayoutAmount);
    const maxStake = getMetadataValue(MetadataType.MaxStake);

    const exceedsLimit = (current: string | undefined, limit: string | undefined): boolean => {
        return Boolean(current && limit && Number(current) >= Number(limit));
    };
    const hasReachedMaxStakeLimit = exceedsLimit(amount, maxStake);
    const hasReachedMaxPayoutLimit = exceedsLimit(profitPayoutAmount, maxProfitPayoutAmount);

    const profitPrefix = isPerfectPlayGameMode ? '+' : 'Up to ';
    const displayAmount = hasEntryAmount ? profitPayoutAmount : '$';
    const maxProfitLabel = hasReachedMaxStakeLimit || hasReachedMaxPayoutLimit ? `(Max)` : '';

    return (
        <>
            <LineSeparator style={styles.horizontalSeparator} />
            <Row>
                <Text variant={'labelMedium'} color={ds.colorToken.text.brand}>
                    {`${profitPrefix}$${displayAmount} Added Profit ${maxProfitLabel}`}
                </Text>
                <Text variant={'labelMedium'} fontWeight={'400'} color={'neutral300'}>
                    • Paid in Betr Bucks
                </Text>
            </Row>
        </>
    );
});

const FreeEntrySection = memo(({ activePromo }: { activePromo?: FantasyEntryPromotionStatus }) => {
    const isPromoFreeEntryApplied = activePromo?.promotion.type === PromotionType.FreeEntry;
    if (!isPromoFreeEntryApplied) return null;

    return (
        <>
            <LineSeparator style={styles.horizontalSeparator} />
            <Row>
                <Text variant={'bodyXSmall'} color={ds.colorToken.text.secondary}>
                    {'Remove the promo to edit your entry amount'}
                </Text>
            </Row>
        </>
    );
});

const FreePickSection = memo(() => {
    const hasFreePick = useBetslipStore(state =>
        state.betslip.some(pick => pick.projection.type === ProjectionType.FreePick)
    );
    if (!hasFreePick) return null;

    return (
        <>
            <LineSeparator style={styles.horizontalSeparator} />
            <Row>
                <Text variant={'labelMedium'} color={ds.colorToken.text.brand}>
                    {'Free Pick Entry'}
                </Text>
                <Text variant={'labelMedium'} fontWeight={'400'} color={'neutral300'}>
                    {' • Profit paid in Playthrough Cash'}
                </Text>
            </Row>
        </>
    );
});

const styles = StyleSheet.create({
    input: {
        fontSize: 17,
        fontWeight: '400',
        flex: 1,
        letterSpacing: -0.43,
        padding: 0,
        color: ds.colorValue.text.primary,
    },
    inputError: {
        color: ds.colorValue.text.danger,
    },
    inputDisabled: {
        pointerEvents: 'none',
    },
    separator: {
        height: 36,
        alignSelf: 'center',
    },
    container: {
        paddingVertical: 9,
    },
    horizontalSeparator: {
        marginVertical: 10,
    },
});
